<template>
  <BaseModal
    :open="true"
    class="repeated-dish-diets-modal"
    :style="{ '--ui-modal-max-width': '800px' }"
    title="Potrawa powtarzająca się"
    :hide-footer="true"
    @close="emit('close')"
  >
    <template #icon>
      <FeaturedIcon
        background-color="color-background-bg-primary"
        icon-color="color-text-text-secondary-700"
        name="Repeat03"
        size="lg"
      />
    </template>

    <Badge class="repeated-dish-diets-modal__dish-name" color="warning" icon="Repeat03" size="sm">
      <strong>{{ dishName }}</strong>
      powtarza się:
    </Badge>

    <div
      class="repeated-dish-diets-modal__columns"
      :class="{
        'repeated-dish-diets-modal__columns--single':
          !currentDietActive || !previousDietsActive
      }"
    >
      <section v-if="currentDietActive" class="repeated-dish-diets-modal__section">
        <h2>{{ currentOccurrences.length }} RAZ(Y) W BIEŻĄCYM JADŁOSPISIE</h2>
        <ul class="repeated-dish-diets-modal__current-occurrences">
          <li v-for="occurrence in currentOccurrences" :key="occurrence.key">
            <Text size="text-sm" color="color-text-text-secondary-700">
              Dzień {{ occurrence.dayIndex + 1 }} – {{ occurrence.mealName }}
            </Text>
          </li>
        </ul>
      </section>

      <section
        v-if="previousDietsActive"
        class="repeated-dish-diets-modal__section repeated-dish-diets-modal__past-section"
      >
        <h2>{{ previousOccurrencesCount }} RAZ(Y) W POPRZEDNICH JADŁOSPISACH</h2>
        <ul class="repeated-dish-diets-modal__past-occurrences">
          <li v-for="repeatedDiet in repeatedDiets" :key="repeatedDiet.id">
            <a :href="repeatedDiet.url" rel="noopener noreferrer" target="_blank">
              <Text size="text-sm" class="repeated-dish-diets-modal__past-occurrence-name">
                {{ repeatedDiet.name }} <br>
                (liczba wystąpień: {{ repeatedDiet.number_of_occurences }})
              </Text>
            </a>
          </li>
        </ul>
      </section>
    </div>
  </BaseModal>
</template>

<script lang="ts" setup>
  import { computed } from 'vue'

  import { Badge, BaseModal, FeaturedIcon, Text } from '@/components/ui'

  import type { RepeatedUsedDiet } from '@/services/dietsService'
  import type { StoreDietMeal } from '@/types/Diet'

  type Props = {
    currentDietActive: boolean
    dishName: string
    meals: StoreDietMeal[]
    previousDietsActive: boolean
    repeatedDiets: RepeatedUsedDiet[]
  }

  const props = defineProps<Props>()
  const emit = defineEmits<{ close: [] }>()

  const currentOccurrences = computed(() =>
    props.meals
      .flatMap((meal, mealIndex) =>
        meal.days.flatMap((day, dayIndex) =>
          day.dishes_and_ingredients
            .filter(
              dishOrIngredient =>
                dishOrIngredient.type === 'dish' && dishOrIngredient.name === props.dishName,
            )
            .map((_, dishOrIngredientIndex) => ({
              dayIndex,
              dishOrIngredientIndex,
              key: `${dayIndex}-${mealIndex}-${dishOrIngredientIndex}`,
              mealIndex,
              mealName: meal.name,
            })),
        ),
      )
      .sort(
        (first, second) =>
          first.dayIndex - second.dayIndex ||
          first.mealIndex - second.mealIndex ||
          first.dishOrIngredientIndex - second.dishOrIngredientIndex,
      ),
  )
  const previousOccurrencesCount = computed(() =>
    props.repeatedDiets.reduce((count, diet) => count + diet.number_of_occurences, 0),
  )
</script>

<style lang="scss" scoped>
  .repeated-dish-diets-modal .ui-modal-footer {
    display: none;
  }

  .repeated-dish-diets-modal__dish-name {
    max-width: 100%;
    overflow-wrap: anywhere;
    white-space: normal;
  }

  .repeated-dish-diets-modal__columns {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    padding: 24px 0;
  }

  .repeated-dish-diets-modal__columns--single {
    grid-template-columns: minmax(0, 1fr);
  }

  .repeated-dish-diets-modal__section {
    min-width: 0;
    padding: 0 16px 0 8px;
  }

  .repeated-dish-diets-modal__past-section {
    padding: 0 8px 0 18px;
    border-left: 1px solid var(--color-tertiary-300);
  }

  .repeated-dish-diets-modal__columns--single .repeated-dish-diets-modal__past-section {
    padding-left: 8px;
    border-left: 0;
  }

  .repeated-dish-diets-modal__section h2 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: var(--color-text-text-secondary-700);
  }

  .repeated-dish-diets-modal__current-occurrences,
  .repeated-dish-diets-modal__past-occurrences {
    display: grid;
    gap: 6px;
    margin: 12px 0 0;
    padding-left: 20px;
  }

  .repeated-dish-diets-modal__past-occurrences li {
    &::marker {
      color: #009e9f;
    }
  }

  .repeated-dish-diets-modal__past-occurrences {
    color: var(--color-text-text-brand-secondary-700);
  }

  .repeated-dish-diets-modal__past-occurrences a {
    color: inherit;
    overflow-wrap: anywhere;
  }

  .repeated-dish-diets-modal__past-occurrences a:hover {
    color: var(--color-text-text-brand-primary-900);
  }

  .repeated-dish-diets-modal__past-occurrence-name {
    color: #009e9f;

    &:hover {
      text-decoration: underline;
    }
  }
</style>
